import type { WalletOption } from './checkout';

// ─── Locale ───────────────────────────────────────────────────────────────────

/**
 * ISO 639-1 two-letter language codes supported by the Express Checkout wallet buttons.
 *
 * Pass one of these to `locale` in the options or to `setLocale()` after mount.
 * Each wallet applies the locale independently:
 * - Apple Pay supports all values in this list.
 * - Google Pay supports most; unsupported languages (e.g. 'ro', 'hu') fall back to 'en'.
 */
export const EXPRESS_LOCALES = [
  'bg', 'cs', 'el', 'en', 'hr', 'hu', 'pl', 'ro', 'sk', 'sl',
] as const;

export type ExpressLocale = typeof EXPRESS_LOCALES[number];

export type ThreeDSecureInitialization = {
  provider: string;
  acquirer_bins: { card_brand: string; acquirer_bin: string }[];
};

/**
 * ISO 639-1 language codes supported by Google Pay's buttonLocale parameter.
 * Source: https://developers.google.com/pay/api/web/reference/request-objects#ButtonOptions
 */
const GPAY_SUPPORTED_LOCALES = new Set([
  'ar', 'bg', 'ca', 'cs', 'da', 'de', 'el', 'en', 'es', 'et',
  'fi', 'fr', 'hr', 'id', 'it', 'ja', 'ko', 'ms', 'nl', 'no',
  'pl', 'pt', 'ru', 'sk', 'sl', 'sr', 'sv', 'th', 'tr', 'uk', 'zh',
]);

/** @internal */
export function normalizeApplePayLocale(locale: string): string {
  return locale || 'en';
}

/** @internal */
export function normalizeGPayLocale(locale: string): string {
  const lang = locale.split('-')[0].toLowerCase();
  return GPAY_SUPPORTED_LOCALES.has(lang) ? lang : 'en';
}

// ─── Line items & shipping ────────────────────────────────────────────────────

/** Line item shown in the Apple Pay payment sheet breakdown. Not used by Google Pay. */
export interface LineItem {
  label: string;
  /** Amount in minor units (e.g. 1200 = 12.00) */
  amount: number;
  type?: 'final' | 'pending';
}

export interface ExpressCheckoutShippingOption {
  id: string;
  label: string;
  detail?: string;
  /** Amount in minor units */
  amount: number;
}

// ─── Button options ───────────────────────────────────────────────────────────

export type ExpressButtonType =
  | 'buy'
  | 'pay'
  | 'plain'
  | 'donate'
  | 'checkout'
  | 'subscribe'
  | 'book'
  | 'order'
  | 'tip'
  | 'contribute';

export type ApplePayButtonStyle = 'black' | 'white' | 'white-outline';
export type GooglePayButtonColor = 'default' | 'black' | 'white';

export interface ExpressButtonOptions {
  /**
   * Button label type shown on both Apple Pay and Google Pay buttons.
   * @default 'plain'
   */
  type?: ExpressButtonType;

  /**
   * Apple Pay button color style.
   * @default 'black'
   */
  applePayStyle?: ApplePayButtonStyle;

  /**
   * Google Pay button color.
   * @default 'default'
   */
  googlePayColor?: GooglePayButtonColor;

  /**
   * Border radius applied to both Apple Pay and Google Pay buttons.
   * Accepts any valid CSS border-radius value (e.g. '0.5rem', '8px', '50%').
   * @default '0.5rem'
   */
  borderRadius?: string;
}

// ─── Options ──────────────────────────────────────────────────────────────────

export interface ExpressCheckoutOptions {
  /** Publishable API key */
  publishableKey: string;

  /** Amount in minor units (e.g. 1200 = €12.00) */
  amount: number;

  /** ISO 4217 currency code (e.g. 'EUR', 'BGN') */
  currency: string;

  /** @default 'payment' */
  mode?: 'payment';

  /**
   * Line items shown in the payment sheet breakdown (subtotal, tax, etc.).
   * Currently displayed by Apple Pay only — Google Pay shows the total only and ignores this field.
   */
  lineItems?: LineItem[];

  /** Initial shipping options. Can be updated via updateWith() */
  shippingOptions?: ExpressCheckoutShippingOption[];

  /** Request shipping address from the wallet sheet */
  requestShipping?: boolean;

  /** Request email from the wallet sheet */
  requestEmail?: boolean;

  /** Request cardholder name from the wallet sheet */
  requestName?: boolean;

  /** ISO 3166-1 alpha-2 country code (e.g. 'DE'). Used in wallet payment requests. */
  countryCode?: string;

  /**
   * ISO 3166-1 alpha-2 country codes the merchant ships to (e.g. ['DE', 'AT', 'CH']).
   * Google Pay only — restricts the address picker so users cannot select unsupported countries.
   * Apple Pay does not support pre-filtering; use `status: 'invalid_shipping_address'`
   * in your `shipping_address_change` handler to reject unsupported addresses at runtime.
   */
  allowedCountries?: string[];

  /** Wallets to enable. Defaults to both Apple Pay and Google Pay */
  walletOptions?: (WalletOption | `${WalletOption}`)[];

  /**
   * Layout of the wallet buttons.
   * - 'row' (default) — buttons side by side, each equal width
   * - 'column' — buttons stacked vertically, each full width
   * - 'auto' — SDK observes container width: column at ≤540px, row above
   */
  layout?: 'column' | 'row' | 'auto';

  /**
   * ISO 639-1 two-letter language code for the wallet button labels (e.g. `'en'`, `'bg'`, `'pl'`).
   * Can be changed after mount via `setLocale()`. Unsupported values fall back to `'en'`.
   * See `ExpressLocale` for the full list of supported codes.
   */
  locale?: ExpressLocale | string;

  /**
   * Customize the wallet button appearance.
   */
  buttonOptions?: ExpressButtonOptions;

  /**
   * Override hosted checkout URL (only available in internal builds).
   * Ignored in production builds.
   */
  hostedCheckoutUrl?: string;

  /**
   * Override Paypercut API URL (only available in internal builds).
   * Ignored in production builds.
   */
  apiUrl?: string;
}

// ─── Update options ───────────────────────────────────────────────────────────

export interface ExpressCheckoutUpdateOptions {
  /** Updated total amount in minor units */
  amount?: number;
  /** Updated line items (breakdown rows). Currently used by Apple Pay only — ignored by Google Pay. */
  lineItems?: LineItem[];
  /** Updated shipping options */
  shippingOptions?: ExpressCheckoutShippingOption[];
  /**
   * Result status to report back to the wallet.
   * Use 'invalid_shipping_address' / 'invalid_shipping_option' to show an error
   * in the wallet sheet without closing it.
   * @default 'success'
   */
  status?: 'success' | 'fail' | 'invalid_shipping_address' | 'invalid_shipping_option';
}

// ─── Event payloads ───────────────────────────────────────────────────────────

export interface ExpressShippingAddress {
  country: string;
  state?: string;
  city?: string;
  postalCode?: string;
}

export interface ShippingAddressChangeEvent {
  address: ExpressShippingAddress;
  /**
   * Call this with updated totals and shipping options.
   * Must be called to unblock the wallet sheet.
   */
  updateWith: (options: ExpressCheckoutUpdateOptions) => void;
}

export interface ShippingOptionChangeEvent {
  shippingOption: ExpressCheckoutShippingOption;
  /**
   * Call this with updated totals.
   * Must be called to unblock the wallet sheet.
   */
  updateWith: (options: ExpressCheckoutUpdateOptions) => void;
}

export interface ExpressTokenPayload {
  id: string;
  type: 'apple_pay' | 'google_pay';
}

export interface ExpressBillingDetails {
  name?: string;
  email?: string;
  phone?: string;
  address?: {
    line1?: string;
    line2?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    country?: string;
  };
}

export interface ExpressShippingDetails {
  name?: string;
  address?: {
    line1?: string;
    line2?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    country?: string;
  };
}

// ─── Session start event ──────────────────────────────────────────────────────

export interface ExpressSessionStartEvent {
  /** Which wallet opened */
  wallet: 'apple_pay' | 'google_pay';
  /**
   * Call this with your initial shipping options and amount.
   * Must be called to unblock the wallet sheet.
   * Identical signature to the updateWith() in shipping change events.
   */
  updateWith: (options: ExpressCheckoutUpdateOptions) => void;
}

// ─── Payment method created / failed events ───────────────────────────────────

export interface ExpressPaymentMethodCreatedEvent {
  /** Paypercut payment method ID — use this server-side to confirm the payment */
  paymentMethodId: string;
  /** Which wallet was used */
  wallet: 'apple_pay' | 'google_pay';
  billingDetails?: ExpressBillingDetails;
  shippingDetails?: ExpressShippingDetails;
  selectedShippingOption?: ExpressCheckoutShippingOption;
}

export interface ExpressPaymentMethodFailedEvent {
  error: string;
}

// ─── Events ───────────────────────────────────────────────────────────────────

export type ExpressEventName =
  | 'ready'                       // wallet buttons rendered
  | 'click'                       // user clicked a wallet button
  | 'session_start'               // before sheet opens — provide initial amount/options via updateWith()
  | 'shipping_options_required'   // session_start timed out — sheet was NOT opened
  | 'shipping_address_change'     // user changed shipping address
  | 'shipping_option_change'      // user changed shipping option
  | 'payment_method_created'      // payment method created — use paymentMethodId server-side to confirm payment
  | 'payment_method_failed'       // payment method creation failed
  | 'cancel'                      // user cancelled the wallet sheet
  | 'error';                      // unrecoverable SDK error

export enum ExpressSdkEvent {
  Ready = 'ready',
  Click = 'click',
  SessionStart = 'session_start',
  ShippingOptionsRequired = 'shipping_options_required',
  ShippingAddressChange = 'shipping_address_change',
  ShippingOptionChange = 'shipping_option_change',
  PaymentMethodCreated = 'payment_method_created',
  PaymentMethodFailed = 'payment_method_failed',
  Cancel = 'cancel',
  Error = 'error',
}

// ─── Instance interface ───────────────────────────────────────────────────────

export interface ExpressCheckoutInstance {
  /** Mount the wallet button iframe into a container */
  mount(container: HTMLElement | string): void;

  /** Subscribe to events. Returns unsubscribe function */
  on(event: 'ready', handler: () => void): () => void;
  on(event: 'click', handler: () => void): () => void;
  on(event: 'session_start', handler: (e: ExpressSessionStartEvent) => void): () => void;
  on(event: 'shipping_options_required', handler: (e: { wallet: 'apple_pay' | 'google_pay' }) => void): () => void;
  on(event: 'shipping_address_change', handler: (e: ShippingAddressChangeEvent) => void): () => void;
  on(event: 'shipping_option_change', handler: (e: ShippingOptionChangeEvent) => void): () => void;
  on(event: 'payment_method_created', handler: (e: ExpressPaymentMethodCreatedEvent) => void): () => void;
  on(event: 'payment_method_failed', handler: (e: ExpressPaymentMethodFailedEvent) => void): () => void;
  on(event: 'cancel', handler: () => void): () => void;
  on(event: 'error', handler: (err: Error) => void): () => void;
  on(event: ExpressEventName, handler: (...args: any[]) => void): () => void;

  /**
   * Push updated totals / shipping options into the wallet sheet.
   * Call this after calculating shipping in response to shipping_address_change
   * or shipping_option_change (via the event's updateWith() helper).
   */
  update(options: ExpressCheckoutUpdateOptions): void;

  /**
   * Dynamically change the button layout after mount.
   * 'row' places buttons side by side, 'column' stacks them vertically.
   */
  setLayout(layout: 'row' | 'column'): void;

  /**
   * Dynamically change the locale of the wallet buttons after mount.
   */
  setLocale(locale: string): void;

  /**
   * Dynamically update button appearance after mount.
   * Merges with existing buttonOptions — only the fields you pass are changed.
   */
  setButtonOptions(opts: Partial<ExpressButtonOptions>): void;

  /** Destroy instance and clean up all listeners */
  destroy(): void;
}

export interface ExpressCheckoutStatic {
  (options: ExpressCheckoutOptions): ExpressCheckoutInstance;
  new(options: ExpressCheckoutOptions): ExpressCheckoutInstance;
}
